import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { Checkbox, IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Delete, Done } from '@mui/icons-material';
import { gridDateTimeFormatter, withQueryParams } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Wrapper } from '@styles/CommonStyles';
import { useTabs } from '@shared/hooks/useTabs';
import { withOpenTab } from '@shared/HOCs/withOpenTab';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { requestDismissDealStatusReminder } from '@modules/ContactManager/components/Header/requests';
import { IDealStatusReminders } from '@shared/components/Layout/components/Header/components/popovers/DealStatusRemindersPreview';
import { requestDeleteDealStatusReminders } from '../requests';

const DealStatusRemindersPage = () => {
  const { userId } = useUserAccount();
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const [loading, setLoading] = useState(false);

  const { data: reminders, mutate } = useSWR(
    userId && `/${SERVICES.NOTIFICATION}/deal-lender-status-notification/${userId}`,
    {
      refreshInterval: 300000,
      revalidateOnFocus: true,
    },
  );

  const handleDismissReminder = useCallback(
    async (reminderId: string, e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setLoading(true);
      await requestDismissDealStatusReminder(reminderId);
      mutate();
      setLoading(false);
    },
    [mutate],
  );

  const handleDeleteReminder = useCallback(
    async (reminderId: string, e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setLoading(true);
      await requestDeleteDealStatusReminders(reminderId);
      mutate();
      setLoading(false);
    },
    [mutate],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      dataGridCol('contactNumericId', { headerName: 'Contact Id' }),
      dataGridCol('contactFirstName', {
        headerName: 'First Name',
        minWidth: 180,
      }),
      dataGridCol('contactLastName', {
        headerName: 'Last Name',
        minWidth: 180,
      }),
      dataGridCol('dealNumericId', {
        headerName: 'Deal ID',
        minWidth: 140,
      }),
      dataGridCol('lenderName', {
        headerName: 'Lender Name',
        minWidth: 280,
      }),
      dataGridCol('dealStatus', {
        headerName: 'Decision Status',
        minWidth: 180,
      }),
      dataGridCol('reminderDate', {
        headerName: 'Status Change On',
        type: 'dateTime',
        minWidth: 200,
        valueFormatter: gridDateTimeFormatter,
      }),
      dataGridCol('actioned', {
        headerName: 'Dismissed',
        minWidth: 120,
        renderCell: (params) => (
          <Checkbox
            disabled
            checked={!!params.row?.actionDate}
          />
        ),
      }),
      dataGridCol('actionDate', {
        headerName: 'Dismissed On',
        width: 200,
        type: 'dateTime',
        valueFormatter: gridDateTimeFormatter,
      }),
      dataGridCol('actions', {
        minWidth: 120,
        renderCell: ({ row }) => (
          <>
            {!row.actionDate && (
              <IconButton
                data-action="dismiss-row"
                title="Dismiss"
                onClick={(e) => handleDismissReminder(row.id, e)}
              >
                <Done
                  sx={{
                    width: '1.8rem',
                    height: '1.8rem',
                  }}
                />
              </IconButton>
            )}
            <IconButton
              data-action="delete-row"
              title="Delete"
              onClick={(e) => handleDeleteReminder(row.id, e)}
            >
              <Delete
                sx={{
                  width: '1.8rem',
                  height: '1.8rem',
                }}
              />
            </IconButton>
          </>
        ),
      }),
    ].map((column) => ({ flex: 1, ...column })),
    [handleDeleteReminder, handleDismissReminder],
  );

  const onReminderClick = useCallback((row: IDealStatusReminders) => {
    const route = withQueryParams('/contact-manager', { id: row.contactId, dealId: row.dealId, tab: 3 }, false);
    const title = `${row.contactFirstName} ${row.contactLastName}`;
    openTab({ title, route });
    navigate(route);
  }, []);

  return (
    <Wrapper>
      <LoaderBackdrop open={loading} />
      <PhoenixGrid
        autoHeight
        withHeader
        noButton
        strict
        disableSelectionOnClick
        showClearAllFilter
        name="Deal Status Reminders"
        columns={columns}
        rows={reminders || []}
        onRowClick={({ row }: { row: IDealStatusReminders }) => onReminderClick(row)}
      />
    </Wrapper>
  );
};

export const DealStatusReminders = withOpenTab(DealStatusRemindersPage, 'Deal Status Reminders');
